import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "fs_admin_token";
const USER_KEY = "fs_admin_user";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t) {
  try { localStorage.setItem(TOKEN_KEY, t); } catch {}
}
export function getUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
}
export function setUser(u) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(u)); } catch {}
}
export function clearAuth() {
  try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); } catch {}
}

const adminApi = axios.create({ baseURL: API });

adminApi.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      clearAuth();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

export async function login(email, password) {
  const { data } = await adminApi.post("/admin/login", { email, password });
  setToken(data.access_token);
  setUser(data.user);
  return data;
}

export async function getMe() {
  const { data } = await adminApi.get("/admin/me");
  return data;
}

export async function getDashboard() {
  const { data } = await adminApi.get("/admin/dashboard");
  return data;
}

export async function listLeads({ limit = 50, skip = 0, fuente, estatus, q } = {}) {
  const params = { limit, skip };
  if (fuente) params.fuente = fuente;
  if (estatus) params.estatus = estatus;
  if (q) params.q = q;
  const { data } = await adminApi.get("/admin/leads", { params });
  return data;
}

export async function getLead(id) {
  const { data } = await adminApi.get(`/admin/leads/${id}`);
  return data;
}

export async function updateStatus(id, estatus) {
  const { data } = await adminApi.patch(`/admin/leads/${id}/status`, { estatus });
  return data;
}

export async function getPipeline({ responsable, fuente, q } = {}) {
  const params = {};
  if (responsable) params.responsable = responsable;
  if (fuente) params.fuente = fuente;
  if (q) params.q = q;
  const { data } = await adminApi.get("/admin/pipeline", { params });
  return data;
}

export async function markLeadAsLost(id, { reason, note } = {}) {
  const { data } = await adminApi.post(`/admin/leads/${id}/lose`, { reason, note });
  return data;
}

export async function rescoreAllLeads() {
  const { data } = await adminApi.post("/admin/leads/rescore-all");
  return data;
}

// ========== ANALYTICS ==========
export async function analyticsSummary({ days = 30 } = {}) {
  const { data } = await adminApi.get("/admin/analytics/summary", { params: { days } });
  return data;
}
export async function analyticsFunnel({ days = 30 } = {}) {
  const { data } = await adminApi.get("/admin/analytics/funnel", { params: { days } });
  return data;
}
export async function analyticsSources({ days = 30 } = {}) {
  const { data } = await adminApi.get("/admin/analytics/sources", { params: { days } });
  return data;
}
export async function analyticsCampaigns({ days = 30, limit = 20 } = {}) {
  const { data } = await adminApi.get("/admin/analytics/campaigns", { params: { days, limit } });
  return data;
}
export async function analyticsDaily({ days = 30 } = {}) {
  const { data } = await adminApi.get("/admin/analytics/daily", { params: { days } });
  return data;
}

// ========== GOOGLE ADS CONVERSION CONFIG ==========
export async function getAdsSettings() {
  const { data } = await adminApi.get("/admin/settings/ads");
  return data;
}
export async function updateAdsSettings(payload) {
  const { data } = await adminApi.put("/admin/settings/ads", payload);
  return data;
}

// ========== CALCULADORA ==========
export async function getCalculatorEstimates(params = {}) {
  const { data } = await adminApi.get("/admin/calculator/estimates", { params });
  return data;
}

export async function getCalculatorStats() {
  const { data } = await adminApi.get("/admin/calculator/stats");
  return data;
}

export async function addNote(id, nota) {
  const { data } = await adminApi.post(`/admin/leads/${id}/notes`, { nota });
  return data;
}

export async function deleteLead(id) {
  const { data } = await adminApi.delete(`/admin/leads/${id}`);
  return data;
}

export async function exportCsvBlob() {
  const res = await adminApi.get("/admin/leads/export/csv", { responseType: "blob" });
  return res.data;
}

export async function testEmail() {
  const { data } = await adminApi.post("/admin/test-email");
  return data;
}

// ========== EMAILS ==========
export async function listEmails({ limit = 100, skip = 0, email_type, status, lead_id } = {}) {
  const params = { limit, skip };
  if (email_type) params.email_type = email_type;
  if (status) params.status = status;
  if (lead_id) params.lead_id = lead_id;
  const { data } = await adminApi.get("/admin/emails", { params });
  return data;
}

export async function emailsStats() {
  const { data } = await adminApi.get("/admin/emails/stats");
  return data;
}

export async function leadEmails(leadId) {
  const { data } = await adminApi.get(`/admin/leads/${leadId}/emails`);
  return data;
}

export async function sendEmailToLead(leadId, { email_type, custom_subject, custom_body } = {}) {
  const { data } = await adminApi.post(`/admin/leads/${leadId}/send-email`, {
    email_type,
    custom_subject,
    custom_body,
  });
  return data;
}

export async function enrichLead(leadId, fields) {
  const { data } = await adminApi.patch(`/admin/leads/${leadId}`, fields);
  return data;
}

// ========== SUBSCRIBERS ==========
export async function listSubscribers({ limit = 100, skip = 0, q, status, ciudad, tipo } = {}) {
  const params = { limit, skip };
  if (q) params.q = q;
  if (status) params.status = status;
  if (ciudad) params.ciudad = ciudad;
  if (tipo) params.tipo = tipo;
  const { data } = await adminApi.get("/admin/subscribers", { params });
  return data;
}

export async function subscribersStats() {
  const { data } = await adminApi.get("/admin/subscribers/stats");
  return data;
}

export async function addSubscriber(sub) {
  const { data } = await adminApi.post("/admin/subscribers", sub);
  return data;
}

export async function deleteSubscriber(id) {
  const { data } = await adminApi.delete(`/admin/subscribers/${id}`);
  return data;
}

export async function uploadSubscribersCsv(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await adminApi.post("/admin/subscribers/upload-csv", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function exportSubscribersCsvBlob() {
  const res = await adminApi.get("/admin/subscribers/export/csv", { responseType: "blob" });
  return res.data;
}

// ========== CAMPAIGNS ==========
export async function listCampaigns({ limit = 50, skip = 0 } = {}) {
  const { data } = await adminApi.get("/admin/campaigns", { params: { limit, skip } });
  return data;
}

export async function getCampaign(id) {
  const { data } = await adminApi.get(`/admin/campaigns/${id}`);
  return data;
}

export async function createCampaign(payload) {
  const { data } = await adminApi.post("/admin/campaigns", payload);
  return data;
}

export async function updateCampaign(id, payload) {
  const { data } = await adminApi.patch(`/admin/campaigns/${id}`, payload);
  return data;
}

export async function deleteCampaign(id) {
  const { data } = await adminApi.delete(`/admin/campaigns/${id}`);
  return data;
}

export async function sendCampaignNow(id) {
  const { data } = await adminApi.post(`/admin/campaigns/${id}/send-now`);
  return data;
}

export async function testCampaign(id, email) {
  const { data } = await adminApi.post(`/admin/campaigns/${id}/test`, { email });
  return data;
}

export async function previewAudience(id) {
  const { data } = await adminApi.post(`/admin/campaigns/${id}/preview-audience`);
  return data;
}

// ========== TEMPLATES ==========
export async function listTemplates() {
  const { data } = await adminApi.get("/admin/email-templates");
  return data;
}

export async function createTemplate(payload) {
  const { data } = await adminApi.post("/admin/email-templates", payload);
  return data;
}

export async function deleteTemplate(id) {
  const { data } = await adminApi.delete(`/admin/email-templates/${id}`);
  return data;
}

export default adminApi;
